import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Stage, Layer, Circle, Text, Group, Rect } from 'react-konva';
import { Box, Button, Typography, CircularProgress, Snackbar, Alert, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { apiHost } from '../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'https' : 'http';
  const baseUrl = apiHost().replace(/^https?:\/\//, '');
  return `${protocol}://${baseUrl}`;
};

const TableLayout = () => {
  const user = useSelector(state => state.auth.user);
  const apiBaseUrl = getApiBaseUrl();
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 900, height: 600 });
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  // Superadmin: denah satu cabang cafe (?cabang=uuid dari halaman Data Meja), supaya meja antar cabang tidak bercampur.
  const superadmin = user?.role === 'superadmin';
  const [searchParams, setSearchParams] = useSearchParams();
  const cabang = searchParams.get('cabang') || '';
  const [cabangs, setCabangs] = useState([]);

  useEffect(() => {
    if (!superadmin) return;
    axios.get(`${apiBaseUrl}/cabang`, { withCredentials: true }).then((r) => {
      const cafe = (r.data?.data || []).filter((c) => c.tipe_cabang === 'cafe');
      setCabangs(cafe);
      if (!cabang && cafe[0]) setSearchParams({ cabang: cafe[0].uuid }, { replace: true });
    }).catch(() => {});
  }, [superadmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTables = useCallback(async () => {
    setLoading(true);
    try {
      if (superadmin && !cabang) { setTables([]); return; }
      const q = superadmin ? `?cabangUuid=${cabang}` : '';
      const res = await axios.get(`${apiBaseUrl}/getmeja${q}`, { withCredentials: true });
      // map tables and ensure numeric positions
      const mapped = (res.data.data || []).map(t => ({
        id: t.id,
        name: t.name,
        x: Number(t.koordinatX) || 50 + (t.id % 5) * 120,
        y: Number(t.koordinatY) || 50 + Math.floor(t.id / 5) * 120,
        raw: t
      }));
      setTables(mapped);
    } catch (err) {
      console.error('fetchTables error', err);
      setSnack({ open: true, message: err.response?.data?.message || 'Gagal memuat meja', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, superadmin, cabang]);

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  // handle container resize
  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        const w = containerRef.current.clientWidth;
        const h = Math.max(500, window.innerHeight - 200);
        setSize({ width: w, height: h });
      }
    };
    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  const handleDragEnd = (e, id) => {
    const node = e.target;
    const newPos = { x: Math.round(node.x()), y: Math.round(node.y()) };
    setTables(prev => prev.map(t => (t.id === id ? { ...t, ...newPos } : t)));
  };

  const savePositions = async () => {
    setSaving(true);
    try {
      const positions = tables.map(t => ({ id: t.id, koordinatX: String(t.x), koordinatY: String(t.y) }));
      await axios.put(`${apiBaseUrl}/updatemeja/positions`, { positions }, { withCredentials: true });
      setSnack({ open: true, message: 'Posisi meja berhasil disimpan', severity: 'success' });
    } catch (err) {
      console.error('savePositions err', err);
      setSnack({ open: true, message: err.response?.data?.message || 'Gagal menyimpan posisi', severity: 'error' });
    } finally {
      setSaving(false);
      fetchTables();
    }
  };

  const resetPositions = () => {
    fetchTables();
  };

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">Akses ditolak</Typography>
        <Typography>Hanya admin / superadmin yang dapat mengatur layout meja.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }} ref={containerRef}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Layout Meja</Typography>
          {superadmin && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="cabang-denah">Cabang</InputLabel>
              <Select labelId="cabang-denah" label="Cabang" value={cabangs.some((c) => c.uuid === cabang) ? cabang : ''}
                onChange={(e) => setSearchParams({ cabang: e.target.value })}>
                {cabangs.map((c) => <MenuItem key={c.uuid} value={c.uuid}>{c.namacabang}</MenuItem>)}
              </Select>
            </FormControl>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" onClick={savePositions} disabled={saving || loading}>
            {saving ? 'Menyimpan...' : 'Simpan Posisi'}
          </Button>
          <Button variant="outlined" color="inherit" onClick={resetPositions} disabled={loading || saving}>
            Reset
          </Button>
        </Box>
      </Box>

      <Box sx={{ border: '1px dashed #ccc', borderRadius: 1, overflow: 'hidden' }}>
        <Stage width={size.width} height={size.height}>
          <Layer>
            {/* background floor */}
            <Rect x={0} y={0} width={size.width} height={size.height} fill="#f8f8f8" />
            {loading ? (
              <Group>
                <Text text="Memuat..." x={20} y={20} fontSize={18} />
              </Group>
            ) : (
              tables.map(t => (
                <Group key={t.id} x={t.x} y={t.y} draggable onDragEnd={(e) => handleDragEnd(e, t.id)}>
                  <Circle radius={36} fill="#1976d2" shadowBlur={6} />
                  <Text text={t.name} fontSize={14} fill="#fff" x={-28} y={-8} width={56} align="center" />
                </Group>
              ))
            )}
          </Layer>
        </Stage>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption">Drag and drop meja untuk mengatur posisinya. Tekan "Simpan Posisi" untuk menyimpan ke server.</Typography>
      </Box>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TableLayout;
